import { useState } from "react";
import { Copy, Check, ExternalLink, ShieldAlert, FileText } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import { useLanguage } from "@/i18n/LanguageContext";
import { projectConfig, explorerTokenUrl, buyTarget } from "@/config/project";

export default function ContractSection() {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(projectConfig.contractAddress);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = projectConfig.contractAddress;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const buyProps = buyTarget.external
    ? { href: buyTarget.href, target: "_blank", rel: "noopener noreferrer" }
    : { href: buyTarget.href };

  return (
    <section id="contract" className="scroll-mt-24 px-4 py-24 sm:px-6 lg:px-8" data-testid="contract-section">
      <div className="mx-auto max-w-4xl">
        <SectionHeader kicker={t("contract.label")} title={t("contract.title")} id="contract" />
        <Reveal>
          <div
            className="rounded-2xl bg-gradient-to-r from-yellow-500/60 via-red-600/50 to-yellow-500/60 p-[1.5px]"
            data-testid="contract-address-card"
          >
            <div className="rounded-2xl bg-[#0D0608] p-6 sm:p-10">
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">{t("contract.subtitle")}</p>
              <p
                className="break-all rounded-lg border border-red-900/40 bg-[#140A0D] px-4 py-4 font-mono text-sm leading-relaxed text-yellow-400 sm:text-base"
                data-testid="contract-address-text"
              >
                {projectConfig.contractAddress}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={copy}
                  data-testid="copy-contract-button"
                  className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-bold tracking-[0.12em] transition-all ${
                    copied
                      ? "bg-green-600 text-white"
                      : "bg-gradient-to-r from-yellow-500 to-yellow-600 text-black hover:-translate-y-0.5"
                  }`}
                  aria-live="polite"
                >
                  {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
                  <span data-testid="copy-contract-label">{copied ? t("contract.copied") : t("contract.copy")}</span>
                </button>
                <a
                  href={explorerTokenUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="view-on-bscscan-button"
                  className="inline-flex items-center gap-2 rounded-full border border-red-700/60 px-6 py-3 text-xs font-bold tracking-[0.12em] text-red-400 transition-colors hover:bg-red-950/40"
                >
                  <ExternalLink size={15} aria-hidden="true" />
                  {t("contract.viewScan")}
                </a>
                <a
                  href={projectConfig.whitepaperUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whitepaper-button"
                  className="inline-flex items-center gap-2 rounded-full border border-zinc-700/70 px-6 py-3 text-xs font-bold tracking-[0.12em] text-zinc-300 transition-colors hover:border-yellow-600/60 hover:text-yellow-400"
                >
                  <FileText size={15} aria-hidden="true" />
                  {t("nav.whitepaper")}
                </a>
                <a
                  {...buyProps}
                  data-testid="contract-buy-sln-button"
                  className="inline-flex items-center gap-2 rounded-full border border-zinc-700/70 px-6 py-3 text-xs font-bold tracking-[0.12em] text-zinc-300 transition-colors hover:border-yellow-600/60 hover:text-yellow-400"
                >
                  {t("contract.buy")}
                </a>
              </div>

              <p className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-zinc-500" data-testid="contract-warning">
                <ShieldAlert size={15} className="mt-0.5 shrink-0 text-yellow-500" aria-hidden="true" />
                {t("contract.warning")}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
